import { types } from "mobx-state-tree";

import { MenuItems } from "../../components/secondary-menu/SecondaryMenu";
import {
  ViewOrganizationAnalysisConfidence,
  ViewOrganizationGisCoordinates,
  ViewOrganizationInfo,
  ViewRemovedUsers,
  ViewOrganizationProjectProperties,
  AddOrganizationProjectProperties,
  ManageDeletedData,
  ManageReportTemplates,
} from "../../services/authorization/Permissions";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";

export enum SettingsMenuItemsPages {
  EditOrganization,
  ViewProjectProperty,
  CreateProjectProperty,
  EditGisCoordinates,
  EditAnalysisConfidence,
}

export const SettingsMenuItemsModel = types
  .model({
    projectPropertyId: types.optional(types.number, 0),
    menuItemPage: types.maybe(types.frozen<SettingsMenuItemsPages>()),
  })
  .actions((self) => {
    return {
      getStaticItems(): MenuItems {
        const menu = {
          "general.general": {
            items: [
              {
                id: "secondary-menu-organization-details",
                labelKey: "general.organization.details",
                path: "/organization-details",
                canAccess: permissionsService.userHasPermissionTo(ViewOrganizationInfo),
                isActive: self.menuItemPage === SettingsMenuItemsPages.EditOrganization,
              },
            ],
            defaultOpen: true,
          },
          "general.platform": {
            items: [
              {
                id: "secondary-menu-project-properties",
                labelKey: "general.project-properties",
                path: "/project-properties",
                canAccess: canAccessProjectDetailsPage(),
                isActive:
                  self.menuItemPage === SettingsMenuItemsPages.ViewProjectProperty ||
                  self.menuItemPage === SettingsMenuItemsPages.CreateProjectProperty,
              },
              {
                id: "secondary-menu-gis-coordinates",
                labelKey: "general.gis-coordinates",
                path: "/gis-coordinates",
                canAccess: permissionsService.userHasPermissionTo(ViewOrganizationGisCoordinates),
                isActive: self.menuItemPage === SettingsMenuItemsPages.EditGisCoordinates,
              },
            ],
            defaultOpen: true,
          },
          "general.removed": {
            items: [
              {
                id: "secondary-menu-removed-users",
                labelKey: "general.removed-users",
                path: "/removed-users",
                canAccess: permissionsService.userHasPermissionTo(ViewRemovedUsers),
              },
            ],
            defaultOpen: true,
          },
        };

        if (featureFlagsStorage.isFeatureEnabled(Flags.Show_Page_Report_Templates)) {
          menu["general.general"].items.push({
            id: "secondary-menu-report-templates",
            labelKey: "general.organization.report-templates",
            path: "/report-templates",
            canAccess: permissionsService.userHasPermissionTo(ManageReportTemplates),
            isActive: false,
          });
        }

        if (featureFlagsStorage.isFeatureEnabled(Flags.Show_Confidence_Level_Controls)) {
          menu["general.platform"].items.push({
            id: "secondary-menu-analysis-confidence",
            labelKey: "general.analysis-confidence",
            path: "/analysis-confidence",
            canAccess: permissionsService.userHasPermissionTo(ViewOrganizationAnalysisConfidence),
            isActive: self.menuItemPage === SettingsMenuItemsPages.EditAnalysisConfidence,
          });
        }

        if (featureFlagsStorage.isFeatureEnabled(Flags.Show_Page_Deleted_Data)) {
          menu["general.removed"].items.push({
            id: "secondary-menu-deleted-data",
            labelKey: "general.deleted-data",
            path: "/deleted-data",
            canAccess: permissionsService.userHasPermissionTo(ManageDeletedData),
          });
        }

        return menu;
      },
    };

    function canAccessProjectDetailsPage(): boolean {
      if (self.menuItemPage === SettingsMenuItemsPages.CreateProjectProperty)
        return permissionsService.userHasPermissionTo(AddOrganizationProjectProperties);
      return permissionsService.userHasPermissionTo(ViewOrganizationProjectProperties);
    }
  });
